/**
 * Two quick actions on a task wherever you plan (Plan my day, Today, the task page):
 * - Move: put it on another day (Tomorrow / Next week / a date / Later / Someday) — the same decisions as Sort.
 * - Step done → next: one task carries the work from start to end. Tick the step you finished, write the next one
 *   and say when; the checklist and history keep the whole story, and the task moves to that day.
 */
import { useQueryClient } from "@tanstack/react-query";
import { CalendarArrowUp, ListChecks, X } from "lucide-react";
import { useState } from "react";
import { post } from "../../lib/api";
import { ErrorNote } from "../ui";

const malta = (d: Date) => d.toLocaleDateString("en-CA", { timeZone: "Europe/Malta" });
const addDays = (day: string, n: number) => { const d = new Date(`${day}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
const dow = (day: string) => new Date(`${day}T12:00:00Z`).getUTCDay();
const nextWorkday = (day: string) => { let d = addDays(day, 1); while ([0, 6].includes(dow(d))) d = addDays(d, 1); return d; };
const nextMonday = (day: string) => addDays(day, ((8 - dow(day)) % 7) || 7);
const label = (day: string) => new Date(`${day}T12:00:00Z`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });

/** Refresh every view that shows planned tasks. */
function useRefresh() {
  const qc = useQueryClient();
  return () => { for (const k of ["plan-today", "plan-day", "my-day", "plan-week", "plan-sort", "work", "came-in", "plan-sort-count"]) void qc.invalidateQueries({ queryKey: [k] }); };
}

/** `plan`: for a task with no "when" yet (Needs you) — Today and This week are offered too. */
export function MoveTask({ id, title, onMoved, compact, plan }: { id: string; title: string; onMoved?: (where: string) => void; compact?: boolean; plan?: boolean }) {
  const refresh = useRefresh();
  const [picking, setPicking] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const go = async (action: string, date?: string) => {
    setError(null);
    try {
      await post(`/api/plan/sort/${id}`, { action, ...(date ? { date } : {}) });
      setPicking(false);
      onMoved?.(action);
      refresh();
    } catch (e) { setError(e); }
  };
  if (picking) {
    return (
      <span className="flex shrink-0 items-center gap-1">
        <input type="date" className="input !w-auto !py-1 text-xs" min={malta(new Date())} autoFocus aria-label={`New day for ${title}`} onChange={(e) => e.target.value && void go("date", e.target.value)} />
        <button type="button" className="btn btn-sm" onClick={() => setPicking(false)} aria-label="Cancel"><X size={13} /></button>
        {!!error && <ErrorNote error={error} />}
      </span>
    );
  }
  return (
    <span className="shrink-0">
      <select
        className={`input !w-auto !py-1 text-xs ${compact ? "!px-1.5" : ""}`} value="" aria-label={plan ? `Plan ${title}` : `Move ${title} to another day`} title={plan ? "Give it a day" : "Move to another day"}
        onChange={(e) => { const v = e.target.value; if (v === "pick") setPicking(true); else if (v) void go(v); }}
      >
        <option value="">{plan ? "Plan…" : "Move…"}</option>
        {plan && <option value="today">Today</option>}
        <option value="tomorrow">Tomorrow</option>
        {plan && <option value="week">This week</option>}
        <option value="nextweek">Next week</option>
        <option value="pick">Pick a day…</option>
        {!plan && <option value="queue">Back to the queue (no day)</option>}
        <option value="later">Later</option>
        <option value="someday">Someday</option>
      </select>
      {!!error && <ErrorNote error={error} />}
    </span>
  );
}

export function NextStepButton({ id, title, current, onDone, className, label, replyCame }: {
  id: string; title: string; current?: string | null; onDone?: (r: { date: string | null; waiting: boolean }) => void; className?: string; label?: string;
  /** The reply you were waiting for came: tick the chase and set the real next step (no "waiting" choice). */
  replyCame?: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className={className ?? "btn btn-sm shrink-0"} onClick={() => setOpen(true)} title={replyCame ? "The reply came: set the next step" : "Step done → next: tick this step, then the next one or wait for a reply"} aria-label={`${replyCame ? "Reply came" : "Step done"} for ${title}`}>
        {replyCame ? null : <ListChecks size={14} />}{label ?? (className ? null : " Step done")}
      </button>
      {open && <NextStepDialog id={id} title={title} current={current ?? null} replyCame={!!replyCame} onClose={() => setOpen(false)} onDone={onDone} />}
    </>
  );
}

function NextStepDialog({ id, title, current, replyCame, onClose, onDone }: { id: string; title: string; current: string | null; replyCame: boolean; onClose: () => void; onDone?: (r: { date: string | null; waiting: boolean }) => void }) {
  const refresh = useRefresh();
  const today = malta(new Date());
  const [done, setDone] = useState(current ?? "");
  const [next, setNext] = useState("");
  const [when, setWhen] = useState<string>(replyCame ? today : nextWorkday(today));
  const [mode, setMode] = useState<"next" | "wait">("next");
  const [on, setOn] = useState("");
  const [until, setUntil] = useState(nextWorkday(nextWorkday(today)));
  const [miss, setMiss] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const quick: Array<[string, string]> = [["Today", today], ["Tomorrow", nextWorkday(today)], ["In 2 days", nextWorkday(nextWorkday(today))], ["Next week", nextMonday(today)]];
  const chase = `Chase ${on.trim() || "…"}: reply on ${done.trim() ? done.trim().charAt(0).toLowerCase() + done.trim().slice(1) : title}`;
  const save = async () => {
    if (mode === "wait" && !on.trim()) { setMiss("on"); return; }
    if (mode === "next" && !next.trim()) { setMiss("next"); return; }
    setBusy(true);
    setError(null);
    try {
      await post(`/api/work/items/${id}/next-step`, mode === "wait"
        ? { done: done.trim() || undefined, wait: { on: on.trim(), until } }
        : { done: done.trim() || undefined, next: next.trim(), ...(when ? { date: when } : {}) });
      refresh();
      onDone?.(mode === "wait" ? { date: until, waiting: true } : { date: when || null, waiting: false });
      onClose();
    } catch (e) { setError(e); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/30 p-0 sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-label={`Next step for ${title}`} onKeyDown={(e) => e.key === "Escape" && onClose()}>
      <div className="w-full max-w-lg space-y-4 rounded-t-2xl bg-[var(--surface,#fff)] p-4 shadow-xl sm:rounded-2xl">
        <div className="flex items-start gap-2">
          <ListChecks size={18} className="mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1"><h2 className="font-semibold">{replyCame ? "Reply came → next" : "Step done → next"}</h2><p className="muted truncate text-sm">{title}</p></div>
          <button type="button" className="btn btn-sm" onClick={onClose} aria-label="Close"><X size={15} /></button>
        </div>
        <label className="block space-y-1 text-sm">
          <span className="section-label">{replyCame ? "Step finished" : "What did you just finish?"}</span>
          <input className="input" value={done} onChange={(e) => setDone(e.target.value)} placeholder={current ? undefined : "e.g. Sent the technical proposal to the client"} maxLength={300} />
          <span className="muted block text-xs">Ticked on the task's checklist, so the task keeps the whole story.</span>
        </label>
        {!replyCame && (
          <div className="grid grid-cols-2 gap-2 text-sm">
            {([["next", "I do the next step"], ["wait", "I'm waiting for a reply"]] as const).map(([m, l]) => (
              <button key={m} type="button" className={`btn btn-sm justify-center ${mode === m ? "!border-brand-600 !bg-brand-50 text-brand-700" : ""}`} aria-pressed={mode === m} onClick={() => { setMode(m); setMiss(""); }}>{l}</button>
            ))}
          </div>
        )}
        {mode === "wait" ? (
          <div className="space-y-3 text-sm">
            <label className="block space-y-1">
              <span className="section-label">Waiting on</span>
              <input className="input" value={on} onChange={(e) => { setOn(e.target.value); setMiss(""); }} placeholder="Client X" maxLength={200} autoFocus onKeyDown={(e) => e.key === "Enter" && void save()} />
              {miss === "on" && <span className="block text-xs text-red-600">Say who you're waiting on</span>}
            </label>
            <div className="space-y-1.5">
              <span className="section-label">Check back on</span>
              <div className="flex flex-wrap items-center gap-1.5">
                {([["Tomorrow", nextWorkday(today)], ["In 2 days", nextWorkday(nextWorkday(today))], ["Next week", nextMonday(today)]] as const).map(([l, d]) => (
                  <button key={l} type="button" className={`chip ${until === d ? "!border-brand-600 !bg-brand-50 text-brand-700" : ""}`} onClick={() => setUntil(d)}>{l}</button>
                ))}
                <input type="date" className="input !w-auto !py-1 text-xs" min={nextWorkday(today)} value={until} onChange={(e) => e.target.value && setUntil(e.target.value)} aria-label="Pick a day" />
              </div>
            </div>
            <p className="muted text-xs">Next step: “{chase}” on {label(until)}. The task leaves your to-do list now and counts as done today; if the reply comes earlier, press Reply came.</p>
          </div>
        ) : (<>
        <label className="block space-y-1 text-sm">
          <span className="section-label">Next step</span>
          <input className="input" value={next} onChange={(e) => { setNext(e.target.value); setMiss(""); }} placeholder="e.g. Ask the client if they want a financial proposal" maxLength={300} autoFocus onKeyDown={(e) => e.key === "Enter" && next.trim() && void save()} />
        </label>
        <div className="space-y-1.5 text-sm">
          <span className="section-label">When will you do it?</span>
          <div className="flex flex-wrap items-center gap-1.5">
            {quick.map(([l, d]) => <button key={l} type="button" className={`chip ${when === d ? "!border-brand-600 !bg-brand-50 text-brand-700" : ""}`} onClick={() => setWhen(d)}>{l}</button>)}
            <input type="date" className="input !w-auto !py-1 text-xs" min={today} value={when} onChange={(e) => setWhen(e.target.value)} aria-label="Pick a day" />
            <button type="button" className={`chip ${!when ? "!border-brand-600 !bg-brand-50 text-brand-700" : ""}`} onClick={() => setWhen("")}>No day</button>
          </div>
          <span className="muted block text-xs">{when ? `The task moves to ${label(when)}${[0, 6].includes(dow(when)) ? " (a weekend)" : ""}.` : "The task keeps its current day."}</span>
        </div>
        {miss === "next" && <span className="block text-xs text-red-600">Write the next step</span>}
        </>)}
        <ErrorNote error={error} />
        <div className="flex gap-2">
          <button type="button" className="btn btn-primary flex-1" disabled={busy} onClick={() => void save()}><CalendarArrowUp size={14} /> {busy ? "Saving…" : mode === "wait" ? "Mark step done, wait" : "Save next step"}</button>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
